import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Link2, Linkedin, Facebook } from "lucide-react";
import { toast } from "sonner";
import { getArticle, getRelated } from "@/data/blog";
import { Seo } from "@/components/Seo";

function Block({ b }) {
  if (b.type === "heading") return <h2 className="font-display text-2xl md:text-3xl mt-12 mb-4">{b.text}</h2>;
  if (b.type === "quote")
    return (
      <blockquote className="my-10 border-l-2 border-[#00B5D8] pl-6 font-display text-xl md:text-2xl text-white/90 leading-relaxed">
        {b.text}
      </blockquote>
    );
  if (b.type === "image")
    return (
      <figure className="my-10 -mx-4 sm:mx-0">
        <div className="rounded-2xl overflow-hidden bg-[#111]">
          <img src={b.src} alt={b.alt} loading="lazy" className="w-full h-full object-cover" />
        </div>
        {b.caption && <figcaption className="mt-3 text-sm text-white/45 text-center">{b.caption}</figcaption>}
      </figure>
    );
  if (b.type === "cta")
    return (
      <div className="my-12 flex flex-wrap gap-4">
        <Link to={b.href} data-testid="blog-cta-primary" className="preshh-btn-primary inline-flex items-center gap-2">
          {b.label} <ArrowRight size={16} />
        </Link>
        {b.secondaryHref && (
          <a href={b.secondaryHref} target="_blank" rel="noreferrer" data-testid="blog-cta-secondary" className="preshh-btn-ghost inline-flex items-center gap-2">
            {b.secondaryLabel}
          </a>
        )}
      </div>
    );
  return <p className="my-5 text-white/75 leading-relaxed text-lg">{b.text}</p>;
}

export default function BlogArticle() {
  const { slug } = useParams();
  const article = getArticle(slug);
  if (!article) return <Navigate to="/blog" replace />;

  const related = getRelated(slug, 3);
  const siteUrl = typeof window !== "undefined" ? window.location.origin : "";
  const url = `${siteUrl}/blog/${article.slug}`;
  const img = article.hero.startsWith("http") ? article.hero : `${siteUrl}${article.hero}`;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: article.title,
    description: article.seoDescription,
    image: img,
    datePublished: article.date,
    author: { "@type": "Organization", name: article.author },
    publisher: { "@type": "Organization", name: "Preshh" },
    mainEntityOfPage: url,
    keywords: (article.keywords || []).join(", "),
  };

  const share = (network) => {
    const u = encodeURIComponent(url);
    const t = encodeURIComponent(article.title);
    const map = {
      x: `https://twitter.com/intent/tweet?url=${u}&text=${t}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${u}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${u}`,
    };
    if (network === "copy") {
      navigator.clipboard?.writeText(url).then(() => toast.success("Link copied")).catch(() => toast.error("Could not copy link"));
      return;
    }
    window.open(map[network], "_blank", "noopener,noreferrer,width=600,height=520");
  };

  return (
    <article className="bg-black text-white min-h-screen" data-testid="blog-article">
      <Seo title={article.seoTitle} description={article.seoDescription} image={img} url={url} type="article" jsonLd={jsonLd} />

      {/* Hero */}
      <header className="relative">
        <div className="aspect-[16/9] md:aspect-[21/9] w-full overflow-hidden bg-[#111]">
          <img src={article.hero} alt={article.heroAlt} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
        </div>
        <div className="max-w-3xl mx-auto px-6 -mt-24 md:-mt-32 relative">
          <Link to="/blog" data-testid="blog-back" className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-white">
            <ArrowLeft size={15} /> Blog
          </Link>
          <p className="mt-4 text-xs uppercase tracking-widest text-[#00B5D8]">{article.category} · {article.dateLabel}</p>
          <h1 className="mt-3 font-display text-4xl sm:text-5xl leading-tight">{article.title}</h1>
          {article.subtitle && <p className="mt-4 text-lg text-white/60">{article.subtitle}</p>}
          <p className="mt-4 text-sm text-white/45">By {article.author}</p>
        </div>
      </header>

      {/* Body */}
      <div className="max-w-3xl mx-auto px-6 pt-10 pb-4">
        {article.content.map((b, i) => <Block key={i} b={b} />)}
      </div>

      {/* Share */}
      <div className="max-w-3xl mx-auto px-6 py-8 border-t border-white/10 flex items-center gap-3">
        <span className="text-sm text-white/50 mr-1">Share</span>
        <button data-testid="share-x" onClick={() => share("x")} aria-label="Share on X" className="w-10 h-10 rounded-full border border-white/15 flex items-center justify-center hover:bg-white/10 transition-colors font-bold">𝕏</button>
        <button data-testid="share-linkedin" onClick={() => share("linkedin")} aria-label="Share on LinkedIn" className="w-10 h-10 rounded-full border border-white/15 flex items-center justify-center hover:bg-white/10 transition-colors"><Linkedin size={16} /></button>
        <button data-testid="share-facebook" onClick={() => share("facebook")} aria-label="Share on Facebook" className="w-10 h-10 rounded-full border border-white/15 flex items-center justify-center hover:bg-white/10 transition-colors"><Facebook size={16} /></button>
        <button data-testid="share-copy" onClick={() => share("copy")} aria-label="Copy link" className="w-10 h-10 rounded-full border border-white/15 flex items-center justify-center hover:bg-white/10 transition-colors"><Link2 size={16} /></button>
      </div>

      {/* Related */}
      {related.length >= 2 && (
        <section className="max-w-7xl mx-auto px-6 py-16 border-t border-white/10">
          <h2 className="font-display text-2xl mb-8">More from Preshh</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {related.map((a) => (
              <Link key={a.slug} to={`/blog/${a.slug}`} data-testid={`related-${a.slug}`} className="group block rounded-2xl overflow-hidden focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00B5D8]">
                <div className="aspect-[4/3] overflow-hidden bg-[#111]">
                  <img src={a.hero} alt={a.heroAlt} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                </div>
                <p className="mt-3 text-xs uppercase tracking-widest text-[#00B5D8]">{a.category}</p>
                <h3 className="mt-1 font-display text-lg leading-snug">{a.title}</h3>
              </Link>
            ))}
          </div>
        </section>
      )}
    </article>
  );
}
